import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  TableCellLayout,
  Button,
  Input,
  useTableFeatures, 
  useTableSort,
  createTableColumn,
  Spinner,
} from "@fluentui/react-components";
import { CiFilter } from "react-icons/ci";
import { Delete20Regular, Add20Filled, Edit20Regular, Diagram20Regular,Flowchart20Regular, Eye20Regular } from "@fluentui/react-icons";
import "../CSS/ListCallFlowsComponent.css";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ConfirmAlert } from "../GenericComponent/ConfirmAlert";
import InfoButton from "../GenericComponent/InfoButton";
import { DeleteChatFlow } from "../../Services/HeedifyServices";
import { getAccessToken } from "../../Services/GraphServices";
import { useMsal } from "@azure/msal-react";
import { useAbility } from "../../Contexts/AbilityContext";
import { useNotification } from '../GenericComponent/NotificationSystem';

const ListChatFlowsComponent = ({ st_ChatFlows, st_WhatsAppFlows, setChatFlows, setWhatsAppFlows, ACTIVE_TENANT }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const userId = activeAccount?.localAccountId;

  const ability = useAbility();
  const { showSuccess, showError } = useNotification();
  
  // Déterminer si on est sur la route WhatsApp
  const isWhatsAppRoute = location.pathname.startsWith("/whatsapp");
  
  // Utiliser les bons flows selon la route
  const currentFlows = isWhatsAppRoute ? st_WhatsAppFlows : st_ChatFlows;
  
  // Définir le chemin de base selon la route
  const basePath = isWhatsAppRoute ? "/whatsapp" : "/chatFlows";
  

  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [selectedChatFlow, setSelectedChatFlow] = useState(null);
  const [loadingDeleteId, setLoadingDeleteId] = useState(null);
  const [filterVisible, setFilterVisible] = useState({ NAME: false, SCOPE: false, TIMEZONE: false, LANGUAGE: false });
  const [filters, setFilters] = useState({ NAME: "", SCOPE: "", TIMEZONE: "", LANGUAGE: "" });

  useEffect(() => {
    if (!ability) return;

    // Permettre la mise à jour même si currentFlows est vide (pour vider la liste)
    if (!currentFlows || currentFlows.length === 0) {
      setRows([]);
      setFilteredRows([]);
      return;
    }

    const enrichedRows = currentFlows.map((flow) => {
      const flowScope = flow.scope || "default";
      return {
        ...flow,
        permissions: {
          main: {
            canCreate: ability.can("create", `Flow.main@${flowScope}`),
            canRead: ability.can("read", `Flow.main@${flowScope}`),
            canUpdate: ability.can("update", `Flow.main@${flowScope}`),
            canDelete: ability.can("delete", `Flow.main@${flowScope}`),
          },
        },
      };
    });

    setRows(enrichedRows);
    setFilteredRows(enrichedRows);
  }, [currentFlows, ability, ACTIVE_TENANT]);

  useEffect(() => {
    let filtered = [...rows];

    if (filters.NAME) {
      filtered = filtered.filter((row) =>
        row.displayName.toLowerCase().includes(filters.NAME.toLowerCase())
      );
    }
    if (filters.SCOPE) {
      filtered = filtered.filter((row) =>
        String(row.scope).toLowerCase().includes(filters.SCOPE.toLowerCase())
      );
    }
    if (filters.TIMEZONE) {
      filtered = filtered.filter((row) =>
        row.timeZone.toLowerCase().includes(filters.TIMEZONE.toLowerCase())
      );
    }
    if (filters.LANGUAGE) {
      filtered = filtered.filter((row) =>
        row.language.toLowerCase().includes(filters.LANGUAGE.toLowerCase())
      );
    }

    setFilteredRows(filtered);
  }, [filters, rows]);

  const columns = useMemo(
    () => [
      createTableColumn({ columnId: "displayName", compare: (a, b) => (a.displayName || "").localeCompare(b.displayName || "") }),
      createTableColumn({ columnId: "scope", compare: (a, b) => String(a.scope || "").localeCompare(String(b.scope || "")) }),
      createTableColumn({ columnId: "timeZone", compare: (a, b) => (a.timeZone || "").localeCompare(b.timeZone || "") }),
      createTableColumn({ columnId: "language", compare: (a, b) => (a.language || "").localeCompare(b.language || "") }),
    ],
    []
  );

  const { getRows, sort: { getSortDirection, toggleColumnSort, sort } } = useTableFeatures(
    { columns, items: filteredRows },
    [useTableSort({ defaultSortState: { sortColumn: "displayName", sortDirection: "ascending" } })]
  );

  const headerSortProps = (columnId) => ({
    onClick: () => toggleColumnSort(columnId),
    sortDirection: getSortDirection(columnId),
  });

  const sortedRows = sort(getRows());

  const toggleFilter = (column) => {
    setFilterVisible((prev) => ({ ...prev, [column]: !prev[column] }));
  };

  const selectChatFlow = (chatFlow) => {
    if (!chatFlow.permissions?.main?.canRead) return;
    navigate(`${basePath}/${chatFlow.id}`);
  };

  const confirmRemove = (chatFlow) => {
    setSelectedChatFlow(chatFlow);
  };

  const removeChatFlow = async (chatFlow) => {
    setLoadingDeleteId(chatFlow.id);
    try {
      const token = await getAccessToken(instance, activeAccount);
      const scope = chatFlow.scope || "default";
      const result = await DeleteChatFlow(ACTIVE_TENANT, userId, chatFlow.id, scope, token);

      if (result?.success === true) {
        const updatedFlows = currentFlows.filter((f) => f.id !== chatFlow.id);
        // Mettre à jour la bonne liste selon le type de flow
        if (isWhatsAppRoute) {
          setWhatsAppFlows(updatedFlows);
        } else {
          setChatFlows(updatedFlows);
        }
        showSuccess(t("CHATFLOW_DELETED_SUCCESS"));
      } else {
        const errorMsg = result?.message || "Erreur inconnue";
        console.error("Échec suppression ChatFlow:", result);
        showError(t("CHATFLOW_DELETE_FAILED"), errorMsg);
      }
    } catch (error) {
      console.error("Error deleting chat flow:", error);
      showError(t("CHATFLOW_DELETE_ERROR"), error?.message);
    } finally {
      setLoadingDeleteId(null);
      setSelectedChatFlow(null);
    }
  };

  const canCreateNew = ability?.can("create", "Flow.main@default");
  return (
    <>
      <div >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h2>{isWhatsAppRoute ? t("WHATSAPP_FLOWS_LIST") : t("CHAT_FLOWS_LIST")}</h2>
        <Button
          icon={<Add20Filled />}
          appearance="primary"
          onClick={() => navigate(`${basePath}/newChatFlow`)}
          disabled={!canCreateNew}
        >
          {isWhatsAppRoute ? t("ADD_WHATSAPP_FLOW") : t("ADD_CHAT_FLOW")}
        </Button>
      </div>

      <div className="table-container">
        <Table sortable>
          <TableHeader>
            <TableRow>
              <TableHeaderCell {...headerSortProps("displayName")}>
                <div className="header-with-filter header-with-filter-center">
                  {t("NAME")}
                  <CiFilter className="filter-icon" onClick={() => toggleFilter("NAME")} />
                </div>
                {filterVisible.NAME && (
                  <Input
                    size="small"
                    placeholder={t("FILTER")}
                    value={filters.NAME}
                    onChange={(e) => setFilters({ ...filters, NAME: e.target.value })}
                  />
                )}
              </TableHeaderCell>
              <TableHeaderCell {...headerSortProps("scope")}>
                <div className="header-with-filter header-with-filter-center">
                  {t("SCOPE")}
                  <CiFilter className="filter-icon" onClick={() => toggleFilter("SCOPE")} />
                </div>
                {filterVisible.SCOPE && (
                  <Input
                    size="small"
                    placeholder={t("FILTER")}
                    value={filters.SCOPE}
                    onChange={(e) => setFilters({ ...filters, SCOPE: e.target.value })}
                  />
                )}
              </TableHeaderCell>
              <TableHeaderCell {...headerSortProps("timeZone")}>
                <div className="header-with-filter header-with-filter-top">
                  {t("TIMEZONE")}
                  <InfoButton infoKey="TIMEZONE" category="CHAT_FLOWS" />
                  <CiFilter className="filter-icon" onClick={() => toggleFilter("TIMEZONE")} />
                </div>
                {filterVisible.TIMEZONE && (
                  <Input
                    size="small"
                    placeholder={t("FILTER")}
                    value={filters.TIMEZONE}
                    onChange={(e) => setFilters({ ...filters, TIMEZONE: e.target.value })}
                  />
                )}
              </TableHeaderCell>
              <TableHeaderCell {...headerSortProps("language")}>
                
                <div className="header-with-filter" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {t("LANGUAGE")}
                  <CiFilter className="filter-icon" onClick={() => toggleFilter("LANGUAGE")} />
                </div>
                {filterVisible.LANGUAGE && (
                  <Input
                    size="small"
                    placeholder={t("FILTER")}
                    value={filters.LANGUAGE}
                    onChange={(e) => setFilters({ ...filters, LANGUAGE: e.target.value })}
                  />
                )}
              </TableHeaderCell>
              <TableHeaderCell className="callflow-list-cell-right">{t("ACTIONS")}</TableHeaderCell>
            </TableRow>
          </TableHeader>

          <TableBody>
            {sortedRows.map(({ item }) => (
              <TableRow key={item.id}>
                <TableCell><TableCellLayout>{item.displayName}</TableCellLayout></TableCell>
                <TableCell><TableCellLayout>{item.scope}</TableCellLayout></TableCell>
                <TableCell><TableCellLayout>{item.timeZone}</TableCellLayout></TableCell>
                <TableCell><TableCellLayout>{item.language}</TableCellLayout></TableCell>
                <TableCell className="callflow-list-cell-right">
                 
                  <Button 
                    icon={item.permissions?.main?.canUpdate ? <Edit20Regular /> : <Eye20Regular />} 
                    appearance="subtle" 
                    disabled={!item.permissions?.main?.canRead} 
                    onClick={() => selectChatFlow(item)} 
                    title={t("EDIT")} 
                  />
                   <Button 
                    icon={<Flowchart20Regular />} 
                    appearance="subtle" 
                    disabled={!item.permissions?.main?.canRead} 
                    onClick={() => navigate(`${basePath}/diagram/${item.id}`)} 
                    title="Diagramme"
                  />
                  <Button 
                    icon={loadingDeleteId === item.id ? <Spinner size="tiny" /> : <Delete20Regular />} 
                    appearance="subtle" 
                    disabled={!item.permissions?.main?.canDelete || loadingDeleteId === item.id} 
                    onClick={() => confirmRemove(item)} 
                    title={t("DELETE")}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {selectedChatFlow && (
        <ConfirmAlert
          open={!!selectedChatFlow}
          name={selectedChatFlow.displayName}
          onConfirm={() => removeChatFlow(selectedChatFlow)}
          onCancel={() => setSelectedChatFlow(null)}
        />
      )}
      </div>
    </>
  );
};

export default ListChatFlowsComponent;
